import { Divider } from "components/atoms/Divider";
import { H2 } from "components/atoms/H2";
import Radio from "components/molecules/radio";
import TalkIcon from "shared/assets/icons/talk.svg";
import { FormProvider, useForm } from "react-hook-form";
import { PinkyRectangle } from "shared/components/atoms/PinkyRectangle";
import tw from "twin.macro";
import { Paths } from "utils/routes";
import { Button } from "shared/components/atoms/Button";
import { OrderButton } from "./OrderButton";
import { useMenuContext } from "../hooks/useMenuContext";
import { Tooltip } from "shared/components/atoms/Tooltip";
import { Rect } from "shared/components/atoms/Rect";
import LightningIcon from "@assets/icons/Lightning.svg";
import { Image } from "shared/components/atoms/Image";
import { Scrollable } from "shared/hooks/useScroll";
import { useCvFromChatToSubscriptionPoc } from "../hooks/useCvFromChatToSubscriptionPoc";
import { useSession } from "next-auth/client";
import { useAuthModal } from "features/shared/hooks/useAuthModal";
import { useRouter } from "next/router";

const Label = tw.label`text-[17px] font-base tracking-[.47px] leading-7`;

const Description = tw.p`text-[9px] tracking-[.24px] leading-none font-normal`;

const Price = tw.span`text-right text-[13px]`;

const HighLight = tw.span`text-[#FE4FD0]`;

const Title = tw.h3`font-semibold text-base leading-4 tracking-[.8px] text-[#0c0f48]`;

const Content = tw.p`font-normal text-2xs leading-[15px] text-[#0c0f48] mt-1`;

export const CvFromChatToSubscriptionPoc = () => {
  const form = useForm({ defaultValues: { type: "subscription" } });
  const { menu } = useMenuContext();
  const { intersectionRef } = useCvFromChatToSubscriptionPoc();
  const [session] = useSession();
  const { showAuthModal } = useAuthModal();
  const { push } = useRouter();

  const selectedMenuType = form.watch("type");
  const isSelectedChat = selectedMenuType === "chat";
  const isSelectedSubscription = selectedMenuType === "subscription";

  const handleChange = (value: string) => {
    form.setValue("type", value);
  };

  const handleSubscribe = async () => {
    if (!session) {
      showAuthModal();
    } else {
      await push(Paths.subscription.plans(8).subscribe.pay);
    }
  };

  return (
    <Scrollable name="CvFromChatToSubscriptionPocElement" ref={intersectionRef}>
      <div tw="px-5 mt-9">
        <H2>鑑定料金・プラン</H2>
        <p tw="text-2xs text-theme leading-7 tracking-[.32px]">
          こちらのメニューはお得な毎月鑑定プランに対応しています
        </p>
        <div tw="border border-solid border-gray rounded-lg overflow-hidden mt-5">
          <FormProvider {...form}>
            <div
              tw="p-4"
              css={isSelectedChat ? tw`bg-white` : tw`bg-[#F2F3F7] pb-2`}
            >
              <Radio
                radioGroupName="type"
                label={
                  <div tw="flex items-end justify-between">
                    <div>
                      <Label>通常プラン</Label>
                      <Description>
                        まずは1回鑑定してほしい方におすすめ
                      </Description>
                    </div>
                    <Price>
                      ¥5,700/鑑定
                      <span tw="block text-[9px] leading-none font-normal mt-0.5">
                        (税込)
                      </span>
                    </Price>
                  </div>
                }
                value="chat"
                onChange={handleChange}
                checked={isSelectedChat}
                inputStyle={
                  isSelectedChat
                    ? tw`bg-theme checked:before:(bg-white bg-none)`
                    : tw`bg-white`
                }
                labelStyle={tw`w-full flex-1`}
              />
              {isSelectedChat && <OrderButton menu={menu} tw="w-full" />}
            </div>
            <Divider tw="bg-gray m-0" />
            <div
              tw="px-4 pt-2 pb-6"
              css={isSelectedSubscription ? tw`bg-white` : tw`bg-[#F2F3F7]`}
            >
              <Radio
                radioGroupName="type"
                label={
                  <div tw="flex items-end justify-between relative">
                    <div>
                      <Label>毎月1回鑑定プラン</Label>
                      <Description>
                        とにかくお得に占いたい方におすすめ
                      </Description>
                    </div>
                    <Price>
                      ¥3,800/鑑定
                      <span tw="block text-[9px] leading-none font-normal mt-0.5">
                        (税込)
                      </span>
                    </Price>
                    <Tooltip
                      tw="absolute -top-6 -right-1"
                      color="#FE4FD0"
                      triangleProps={{
                        direction: "bottom",
                        width: 12,
                        height: 4,
                        style: { position: "absolute", bottom: -9, right: 32 },
                      }}
                    >
                      ¥1900もお得
                    </Tooltip>
                  </div>
                }
                value="subscription"
                onChange={handleChange}
                checked={isSelectedSubscription}
                inputStyle={
                  isSelectedSubscription
                    ? tw`bg-[#FE4FD0] checked:before:(bg-white bg-none)`
                    : tw`bg-white`
                }
                labelStyle={tw`w-full flex-1`}
              />
              <PinkyRectangle tw="rounded-md font-bold text-[11px] tracking-[.5px] leading-6 flex-center flex-col mt-5">
                <div tw="bg-[#FE4FD0] rounded-full text-center inline-block px-3 border-[1.5px] border-solid border-white">
                  <p tw="text-white mt-[1px]">毎月1回鑑定プランって？</p>
                </div>
                <p tw="mt-1 text-center">
                  <HighLight>毎月1回お得に占えるプラン</HighLight>です！
                  <br />
                  通常価格よりお得なのに
                  <HighLight>サービス内容は同じ</HighLight>!
                  <br />
                  しかも<HighLight>いつでも解約OK</HighLight>
                  なのでとにかくお得！
                </p>
              </PinkyRectangle>
              {isSelectedSubscription && (
                <Button
                  tw="bg-[#FE4FD0] w-full mt-6 rounded-lg h-12"
                  onClick={handleSubscribe}
                >
                  <TalkIcon
                    tw="mr-1.5"
                    width={18.7}
                    height={17.5}
                    viewBox="0, 0, 9.35, 9.5"
                  />
                  鑑定に進む
                </Button>
              )}
            </div>
          </FormProvider>
        </div>
        <Rect tw="rounded-3xl py-8 px-4 border-2 border-[#FE4FD0] bg-white items-center text-center mt-8">
          <LightningIcon
            width={50}
            viewBox="0 0 27 35"
            tw="svg absolute -top-4 text-[#FE4FD0] bg-white"
          />
          <Title>毎月1回鑑定プランとは</Title>
          <Content tw="mt-3">
            通常プランと同じ内容の鑑定が毎月1回までお得に鑑定してもらえるサブスクプランです。
          </Content>
          <Image
            src={`${process.env.NEXT_PUBLIC_CDN_V2}/menu/payment_cycle.png`}
            layout="dynamic"
            tw="mt-2"
          />
          <Content tw="mt-6">
            このプランは毎月自動更新されますが、
            <HighLight tw="font-semibold">1か月目でも解約できる</HighLight>
            ので、
            <HighLight tw="font-semibold">
              誰でも必ずお得に鑑定が受けられます!
            </HighLight>
          </Content>
          <Title tw="mt-6">1ヶ月だけの利用でもお得に</Title>
          <Image
            src={`${process.env.NEXT_PUBLIC_CDN_V2}/menu/cost_simulation.png`}
            layout="dynamic"
            tw="mt-[18px]"
          />
        </Rect>
      </div>
    </Scrollable>
  );
};
